import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ChevronLeft, ChevronRight, Plus, X, User, Building } from 'lucide-react';
import { toast } from 'sonner';

export const AddReferralsStep = ({ formData, updateFormData, onNext, onBack }) => {
  const [referrals, setReferrals] = useState(formData.referrals.length > 0 ? formData.referrals : [
    { firstName: '', lastName: '', email: '', phone: '', type: '', services: [], message: '', sendEmail: false }
  ]);

  const serviceOptions = [
    { value: 'tax_prep', label: 'Tax preparation & planning' },
    { value: 'bookkeeping', label: 'Bookkeeping & accounting' },
    { value: 'irs_issues', label: 'IRS or tax issues' },
    { value: 'advisory', label: 'Business advisory' },
    { value: 'entity_setup', label: 'Entity setup' },
    { value: 'payroll', label: 'Payroll services' },
    { value: 'not_sure', label: 'Not sure' }
  ];

  const updateReferral = (index, field, value) => {
    const updated = [...referrals];
    updated[index][field] = value;
    setReferrals(updated);
  };

  const toggleService = (index, service) => {
    const updated = [...referrals];
    const services = updated[index].services || [];
    if (services.includes(service)) {
      updated[index].services = services.filter(s => s !== service);
    } else {
      updated[index].services = [...services, service];
    }
    setReferrals(updated);
  };

  const addReferral = () => {
    if (referrals.length >= 5) {
      toast.error('Maximum 5 referrals per submission');
      return;
    }
    setReferrals([...referrals, { firstName: '', lastName: '', email: '', phone: '', type: '', services: [], message: '', sendEmail: false }]);
  };

  const removeReferral = (index) => {
    if (referrals.length === 1) {
      toast.error('You must refer at least one person');
      return;
    }
    setReferrals((referrals || []).filter((_, i) => i !== index));
  };

  const handleNext = () => {
    // Validate all referrals
    for (let i = 0; i < referrals.length; i++) {
      const ref = referrals[i];
      if (!ref.firstName || !ref.lastName || !ref.email || !ref.type) {
        toast.error(`Referral ${i + 1}: Please fill in name, email, and type`);
        return;
      }
      if (!ref.email.includes('@')) {
        toast.error(`Referral ${i + 1}: Invalid email address`);
        return;
      }
    }

    updateFormData({ referrals });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0B2240] mb-2">
          Who Would You Like to Refer?
        </h2>
        <p className="text-[#4C5A70]">
          Add up to 5 people who might benefit from KGOB's services
        </p>
      </div>

      {(referrals || []).map((referral, index) => (
        <Card key={index} className="p-6 border-2 border-[#E1DFD7] relative">
          {referrals.length > 1 && (
            <button data-testid="add-referrals-step-button-1"
              onClick={() => removeReferral(index)}
              className="absolute top-4 right-4 text-[#4C5A70] hover:text-red-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          
          <p className="font-medium text-[#0B2240] mb-4">Referral {index + 1}</p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>First Name *</Label>
              <Input data-testid="add-referrals-step-input-4"
                value={referral.firstName}
                onChange={(e) => updateReferral(index, 'firstName', e.target.value)}
                placeholder="Jane"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input data-testid="add-referrals-step-input-3"
                value={referral.lastName}
                onChange={(e) => updateReferral(index, 'lastName', e.target.value)}
                placeholder="Smith"
                className="mt-2"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Email *</Label>
              <Input data-testid="add-referrals-step-input-2"
                type="email"
                value={referral.email}
                onChange={(e) => updateReferral(index, 'email', e.target.value)}
                placeholder="jane@example.com"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input data-testid="add-referrals-step-input-1"
                type="tel"
                value={referral.phone}
                onChange={(e) => updateReferral(index, 'phone', e.target.value)}
                placeholder="(555) 987-6543"
                className="mt-2"
              />
            </div>
          </div>

          <div className="mb-4">
            <Label>This referral is for: *</Label>
            <RadioGroup value={referral.type} onValueChange={(value) => updateReferral(index, 'type', value)} className="mt-2">
              <div className="grid md:grid-cols-2 gap-3">
                <div data-testid="add-referrals-step-div-2" className="flex items-center space-x-3 p-3 rounded-lg border-2 border-[#E1DFD7] hover:border-[#D4AF37] cursor-pointer"
                     onClick={() => updateReferral(index, 'type', 'individual')}>
                  <RadioGroupItem value="individual" id={`individual-${index}`} />
                  <Label htmlFor={`individual-${index}`} className="flex items-center gap-2 cursor-pointer">
                    <User className="w-4 h-4 text-[#D4AF37]" />
                    <span>Individual/Family</span>
                  </Label>
                </div>
                <div data-testid="add-referrals-step-div-1" className="flex items-center space-x-3 p-3 rounded-lg border-2 border-[#E1DFD7] hover:border-[#D4AF37] cursor-pointer"
                     onClick={() => updateReferral(index, 'type', 'business')}>
                  <RadioGroupItem value="business" id={`business-${index}`} />
                  <Label htmlFor={`business-${index}`} className="flex items-center gap-2 cursor-pointer">
                    <Building className="w-4 h-4 text-[#D4AF37]" />
                    <span>Business Owner</span>
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="mb-4">
            <Label>What might they need help with? (Optional)</Label>
            <div className="grid md:grid-cols-2 gap-2 mt-2">
              {serviceOptions.map(option => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox data-testid="add-referrals-step-checkbox-2"
                    checked={referral.services?.includes(option.value)}
                    onCheckedChange={() => toggleService(index, option.value)}
                    id={`service-${index}-${option.value}`}
                  />
                  <Label htmlFor={`service-${index}-${option.value}`} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <Label>Personal note (optional)</Label>
            <Textarea data-testid="add-referrals-step-textarea-1"
              value={referral.message}
              onChange={(e) => updateReferral(index, 'message', e.target.value)}
              placeholder="e.g., They mentioned struggling with quarterly taxes..."
              maxLength={200}
              className="mt-2 min-h-[80px]"
            />
            <p className="text-xs text-[#4C5A70] mt-1">{referral.message?.length || 0}/200</p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox data-testid="add-referrals-step-checkbox-1"
              checked={referral.sendEmail}
              onCheckedChange={(checked) => updateReferral(index, 'sendEmail', checked)}
              id={`sendEmail-${index}`}
            />
            <Label htmlFor={`sendEmail-${index}`} className="text-sm cursor-pointer">
              Send introduction email to {referral.firstName || 'this person'} on my behalf
            </Label>
          </div>
        </Card>
      ))}

      {referrals.length < 5 && (
        <Button data-testid="add-referrals-step-button-3"
          onClick={addReferral}
          variant="outline"
          className="w-full h-12 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Referral ({referrals.length}/5)
        </Button>
      )}

      <div className="flex gap-4 mt-8">
        <Button data-testid="add-referrals-step-button-2"
          onClick={onBack}
          variant="outline"
          className="h-12 px-6 border-2 border-[#0B2240] text-[#0B2240]"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button data-testid="add-referrals-step-button-1"
          onClick={handleNext}
          disabled={referrals.length === 0}
          className="flex-1 h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
        >
          Continue to Review
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};